/**
 * MentalEmotionalTwinPage — iter629 — Психо-емоционален близнак (15-ти).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, Heart, Plus, ListChecks } from "lucide-react";
import { toast } from "sonner";
import VoiceBiomarkersCard from "@/components/VoiceBiomarkersCard";
import { MobileTopBar } from "@/components/MobileTopBar";
import DailyCheckinCard from "@/components/shared/DailyCheckinCard";  // iter1054
import { twinLabel } from "@/lib/twinCatalog";

const PHQ9_OPTIONS = ["Никога (0)", "Няколко дни (1)", "Повече от половината дни (2)", "Почти всеки ден (3)"];

export default function MentalEmotionalTwinPage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mood, setMood] = useState(7);
  const [energy, setEnergy] = useState(7);
  const [gratitude, setGratitude] = useState("");
  const [showQuestionnaire, setShowQuestionnaire] = useState(null); // 'phq9' | 'gad7' | null
  const [answers, setAnswers] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/mental-twin/snapshot`);
      setSnap(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [API]);

  const submitCheckIn = async () => {
    try {
      await axios.post(`${API}/mental-twin/check-in`, {
        mood_1_10: mood, energy_1_10: energy, gratitude_text: gratitude || null,
      });
      window.dispatchEvent(new Event("mental-log-changed"));
      window.dispatchEvent(new Event("twin-score-changed"));
      toast.success("Записано! +1 ден check-in.");
      setGratitude("");
      load();
    } catch { toast.error("Грешка."); }
  };

  const openQuestionnaire = (kind) => {
    const count = kind === "phq9" ? 9 : 7;
    setAnswers(new Array(count).fill(0));
    setShowQuestionnaire(kind);
  };

  const submitQuestionnaire = async () => {
    try {
      const r = await axios.post(`${API}/mental-twin/${showQuestionnaire}`, { scores: answers });
      window.dispatchEvent(new Event("mental-log-changed"));
      window.dispatchEvent(new Event("twin-score-changed"));
      toast.success(`Резултат: ${r.data.total} (${r.data.severity_bg})`);
      setShowQuestionnaire(null);
      load();
    } catch { toast.error("Грешка."); }
  };

  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";
  const questions = showQuestionnaire === "phq9" ? snap?.phq9_questions_bg : snap?.gad7_questions_bg;

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`} data-testid="mental-twin-page">
      <MobileTopBar title="Настроение" fallbackTo="/dashboard" testid="stress-twin-mobile-topbar" />
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <button onClick={() => navigate("/dashboard")} className="text-xs opacity-60 hover:opacity-100 mb-3 flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Към таблото</button>
        <div className="flex items-center gap-2 mb-3">
          <Heart className="w-5 h-5 text-violet-500" />
          <h1 className="text-xl font-bold">{twinLabel("stress")}</h1>
        </div>
        <p className={`text-xs mb-4 ${isDark ? "text-slate-400" : "text-slate-600"}`}>15-сек дневен check-in + валидирани скрининг скали PHQ-9 (депресия) и GAD-7 (тревожност).</p>

        {/* iter1054 — един вход за три близнака */}
        <div className="mb-4">
          <DailyCheckinCard isDark={isDark} />
        </div>

        {loading ? <Loader2 className="w-6 h-6 animate-spin text-violet-500 mx-auto my-12" /> : (
          <>
            <div className={`p-5 rounded-2xl border-2 mb-4 ${cardCls}`} data-testid="mental-snapshot">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-4xl font-bold" data-testid="mental-score">{snap?.composite ?? "—"}</span>
                <span className="text-xs opacity-60">/100</span>
              </div>
              <p className="text-sm mt-2" data-testid="mental-interpretation">{snap?.interpretation_bg}</p>
              <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                <div className={`p-2 rounded-lg ${isDark ? "bg-slate-800/50" : "bg-slate-100"}`}><div className="opacity-60 text-[10px]">Mood avg 7д</div><div className="font-bold">{snap?.mood_avg_7d ?? "—"}/10</div></div>
                <div className={`p-2 rounded-lg ${isDark ? "bg-slate-800/50" : "bg-slate-100"}`}><div className="opacity-60 text-[10px]">PHQ-9</div><div className="font-bold">{snap?.phq9_total ?? "—"}/27</div></div>
                <div className={`p-2 rounded-lg ${isDark ? "bg-slate-800/50" : "bg-slate-100"}`}><div className="opacity-60 text-[10px]">GAD-7</div><div className="font-bold">{snap?.gad7_total ?? "—"}/21</div></div>
              </div>
            </div>

            {showQuestionnaire ? (
              <div className={`p-5 rounded-2xl border-2 ${cardCls}`} data-testid="questionnaire-form">
                <h2 className="font-bold text-sm mb-3">{showQuestionnaire === "phq9" ? "PHQ-9 (Депресия)" : "GAD-7 (Тревожност)"}</h2>
                <p className="text-[11px] opacity-70 mb-3">През последните 2 седмици колко често ви безпокоят следните проблеми?</p>
                <div className="space-y-3">
                  {(questions || []).map((q, i) => (
                    <div key={i} className={`p-3 rounded-lg ${isDark ? "bg-slate-800/40" : "bg-slate-50"}`} data-testid={`q-${i}`}>
                      <div className="text-sm font-medium mb-2">{i + 1}. {q}</div>
                      <div className="grid grid-cols-4 gap-1">
                        {PHQ9_OPTIONS.map((opt, j) => (
                          <button key={j} onClick={() => { const a = [...answers]; a[i] = j; setAnswers(a); }}
                            className={`px-2 py-1.5 rounded-md text-[10px] transition-colors ${answers[i] === j ? "bg-violet-500 text-white font-bold" : isDark ? "bg-slate-700 hover:bg-slate-600" : "bg-white hover:bg-slate-100 border border-slate-300"}`}
                            data-testid={`q-${i}-opt-${j}`}>
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={submitQuestionnaire} className="bg-violet-600 hover:bg-violet-700 text-white flex-1" data-testid="submit-questionnaire-btn">Изчисли резултат</Button>
                  <Button onClick={() => setShowQuestionnaire(null)} variant="outline">Отказ</Button>
                </div>
              </div>
            ) : (
              <>
                <div className={`p-5 rounded-2xl border mb-4 ${cardCls}`} data-testid="checkin-form">
                  <h2 className="font-bold text-sm mb-3 flex items-center gap-1"><Plus className="w-4 h-4" /> Дневен check-in (15 сек)</h2>
                  <label className="block mb-3">
                    <div className="flex justify-between text-xs"><span className="opacity-70">Настроение</span><span className="font-bold">{mood}/10</span></div>
                    <input type="range" min="1" max="10" value={mood} onChange={(e) => setMood(parseInt(e.target.value))} className="w-full accent-violet-500" data-testid="mood-slider" />
                  </label>
                  <label className="block mb-3">
                    <div className="flex justify-between text-xs"><span className="opacity-70">Енергия</span><span className="font-bold">{energy}/10</span></div>
                    <input type="range" min="1" max="10" value={energy} onChange={(e) => setEnergy(parseInt(e.target.value))} className="w-full accent-violet-500" data-testid="energy-slider" />
                  </label>
                  <label className="block mb-3">
                    <span className="text-[10px] opacity-70">За какво си благодарен/а днес? (опционално)</span>
                    <textarea value={gratitude} onChange={(e) => setGratitude(e.target.value)} rows={2} className={`w-full px-2 py-1.5 rounded-md border text-sm ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`} data-testid="gratitude-text" />
                  </label>
                  <Button onClick={submitCheckIn} className="bg-violet-600 hover:bg-violet-700 text-white w-full" data-testid="submit-checkin-btn">Записва check-in</Button>
                </div>

                <div className="flex gap-2">
                  <Button onClick={() => openQuestionnaire("phq9")} variant="outline" className="flex-1" data-testid="start-phq9-btn">
                    <ListChecks className="w-4 h-4 mr-1" /> PHQ-9
                  </Button>
                  <Button onClick={() => openQuestionnaire("gad7")} variant="outline" className="flex-1" data-testid="start-gad7-btn">
                    <ListChecks className="w-4 h-4 mr-1" /> GAD-7
                  </Button>
                </div>

                <div className="mt-4">
                  <VoiceBiomarkersCard />
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
